// directive-redirect-ticket-commit.ts
//
// Re-review 2026-09-26 (area B, F-B1): commits the ticket files a redirect
// just wrote. F2 (`applyRedirectDirective`, proposed_changes) used to stop at
// `writeTicketFile`; the next dispatched tick's recoverOwnedCheckout
// (`git clean -fd`, supervisor-pre-dispatch-checks.ts) then erased the
// untracked file while the journal already said "applied". Same contract F3
// (redirect-text-to-decision-ticket.ts) follows: commit in the SAME tick,
// through commit-owner (the only sanctioned commit path), and surface a
// policy denial as a rejection reason instead of leaving a doomed file.
//
// Phase 06f R2 (L8, spec E29): also the ONE place both redirect routes
// (structured + text ⇒ decision ticket) rebuild `.avatar/PLAN.md` and put it
// in the same commit — a redirect that only cancels/supersedes writes no
// ticket file but still changes the DAG every `plan_path` points at.
//
// Security audit M-1: commit-owner SKIPS a pathspec it cannot stage (an
// ignore rule a worker left) and still reports success. Every ticket file is
// therefore checked AT HEAD, byte for byte, before the caller may journal
// `applied`.
import { randomUUID } from "node:crypto";
import { join } from "node:path";
import { commitTicket, type GitRunner } from "./commit-owner.js";
import { PLAN_INDEX_PATH, writePlanIndex } from "./controller-plan-index-markdown.js";
import type { MissionStore } from "./mission-store.js";
import type { PolicyAdapterInput, PolicyVerdict } from "./policy-adapter.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { ticketFileName } from "./ticket-file-name.js";
import { ticketsNotCommittedReason } from "./ticket-files-at-head-commit-check.js";

export interface CommitRedirectTicketFilesInput {
  store: MissionStore;
  repoDir: string;
  gitRunner: GitRunner;
  evaluatePolicy: (input: PolicyAdapterInput) => Promise<PolicyVerdict>;
  missionId: string;
  directiveId: string;
  /** Tickets whose files were just (re)written by the redirect — may be empty. */
  tickets: Ticket[];
  /** Stale sibling files a title rename removed (writeTicketFile's `removed`) — staged so the commit carries the deletion. */
  removedPaths: string[];
  /** 06f R2: the DAG after the redirect — PLAN.md is rebuilt from it and committed with the tickets. */
  planIndex?: {
    tickets: readonly Ticket[];
    status: Record<string, string>;
    /** Ids for the commit subject/ref when `tickets` is empty (cancel/supersede only). */
    ticketIds?: string[];
  };
  /** Commit subject override (redirect text keeps `redirect <id> -> decision ticket`). */
  subject?: string;
}

export type CommitRedirectTicketFilesResult = { ok: true } | { ok: false; reason: string };

/**
 * `git add -- <path>` rejects a pathspec that matches nothing, and a stale
 * sibling that was never committed (no bootstrap commit yet) is simply gone
 * once removed — only TRACKED siblings need their deletion staged.
 */
async function trackedSubset(
  gitRunner: GitRunner,
  repoDir: string,
  paths: string[],
): Promise<string[]> {
  if (paths.length === 0) return [];
  const res = await gitRunner(["ls-files", "--", ...paths], repoDir);
  if (res.status !== 0) return [];
  return res.stdout
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * No ticket file and no `planIndex` (gate-only redirect) ⇒ no commit. A replay
 * that rewrites identical bytes stages nothing ⇒ `no_change`, never an empty commit.
 */
export async function commitRedirectTicketFiles(
  input: CommitRedirectTicketFilesInput,
): Promise<CommitRedirectTicketFilesResult> {
  if (input.tickets.length === 0 && input.planIndex === undefined) return { ok: true };
  const ids =
    input.tickets.length > 0 ? input.tickets.map((t) => t.id) : (input.planIndex?.ticketIds ?? []);
  const ticketId = ids[0];
  // Review 06f L4: both callers pass ≥ 1 id today; an empty list is refused
  // BEFORE PLAN.md is written (no uncommitted file for the next tick's reset).
  if (ticketId === undefined) {
    return {
      ok: false,
      reason: "redirect_commit_without_ticket_id: không có ticket nào để gắn commit",
    };
  }
  if (input.planIndex !== undefined) {
    await writePlanIndex(input.repoDir, input.planIndex.tickets, input.planIndex.status);
  }
  const removedTracked = await trackedSubset(input.gitRunner, input.repoDir, input.removedPaths);
  const ticketPaths = input.tickets.map((t) => join(".avatar", "tickets", ticketFileName(t)));
  const commit = await commitTicket({
    store: input.store,
    repoDir: input.repoDir,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    manifestPaths: [
      ...ticketPaths,
      ...removedTracked,
      ...(input.planIndex === undefined ? [] : [PLAN_INDEX_PATH]),
    ],
    missionId: input.missionId,
    runId: randomUUID(),
    ticketId,
    type: "chore",
    scope: "autopilot",
    subject: input.subject ?? `redirect ${input.directiveId} -> ${ids.join(", ")}`,
  });
  if (commit.status === "policy_denied") {
    return { ok: false, reason: `commit ticket bị policy từ chối: ${commit.reason}` };
  }
  const notCommitted = await ticketsNotCommittedReason(
    input.gitRunner,
    input.repoDir,
    input.tickets,
  );
  return notCommitted === undefined ? { ok: true } : { ok: false, reason: notCommitted };
}
